import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Plus, Search, X, Upload, Download, Pencil, KeyRound } from 'lucide-react';
import API from '../api';
import { showSuccess, showError } from '../utils/notification';
import { useAuth } from '../auth/AuthContext';

// Admin-only page: maintain Department Users (Level 1 / Level 2 officers).
// FRS rule: every officer mapping must include a section. Routing fails
// otherwise, so the form enforces section selection.
export default function DepartmentUserMaster() {
  const navigate = useNavigate();
  const { user: userInfo, role: userRole, isReady: authReady, isAuthenticated } = useAuth();
  const isAdmin = userRole === 'admin';
  const isDeptAdmin = userRole === 'dept_admin';
  // K-Labs Admin: everything unlocked (any dept, any district).
  // Dept Admin: department is locked to their own; district is UNLOCKED
  //   (they manage every district under their department).
  const lockedDeptId = isDeptAdmin ? userInfo?.department_id : null;
  const lockedDistId = null;

  const [officers, setOfficers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingMappingId, setEditingMappingId] = useState(null);

  // Bulk upload UI state.
  const [showBulk, setShowBulk] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null); // { total, ready, failed, rows: [] }
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [form, setForm] = useState({
    email_id: '',
    username:      '',     // WRD only — login id
    full_name: '',
    password: '',
    department_id: lockedDeptId || '',
    district_id: lockedDistId || '',
    section_name:  '',
    block_name:    '',
    division_name: '',
    ulb_name:      '',
    region_name:   '',
    office_name:   '',     // WRD only
    designation:   '',     // WRD only
    role_type:     '',     // WRD only (auto-set from designation)
    level: 1,
  });

  // When authenticated user finally loads (or their dept id changes),
  // sync the locked Department field. Dept Admins can't change it, so
  // if the form state is empty we auto-fill it from the JWT.
  useEffect(() => {
    if (isDeptAdmin && lockedDeptId && !form.department_id) {
      setForm((prev) => ({ ...prev, department_id: lockedDeptId }));
    }
  }, [isDeptAdmin, lockedDeptId, form.department_id]);

  // Block options come from block_mst (authoritative master) so the
  // dropdown can never carry a value the backend will reject. We still
  // narrow by the chosen district using tank cross-reference so the user
  // only sees blocks that exist in their district.
  const [blockOptions, setBlockOptions] = useState([]);
  const [allBlocks, setAllBlocks] = useState([]);
  useEffect(() => {
    // Load full block master once.
    API.get('/blocks')
      .then((r) => setAllBlocks(Array.isArray(r?.data?.data) ? r.data.data : []))
      .catch(() => setAllBlocks([]));
  }, []);
  useEffect(() => {
    if (!form.district_id) { setBlockOptions([]); return; }
    const dist = districts.find((d) => Number(d.district_id) === Number(form.district_id));
    if (!dist) { setBlockOptions([]); return; }
    // Get the block names valid for this district (via the tank dataset)…
    API.get('/tanks/filter-options', { params: { field: 'block', district_name: dist.district_name }, silent: true })
      .then((r) => {
        const names = (Array.isArray(r?.data?.data) ? r.data.data : []).map((n) => String(n).trim().toLowerCase());
        // …and intersect with the master so only known blocks are pickable.
        const masterByLower = new Map(allBlocks.map((b) => [String(b.block_name).trim().toLowerCase(), b]));
        const valid = names.map((n) => masterByLower.get(n)).filter(Boolean);
        setBlockOptions(valid);
      })
      .catch(() => setBlockOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.district_id, districts, allBlocks]);

  // Division / ULB / Region masters — used by GCC / DMA / HR&CE respectively.
  // We fetch the full master once, then narrow by tank data in the chosen
  // district so the dropdown only shows values that actually exist for
  // that district.
  const [allDivisions, setAllDivisions] = useState([]);
  const [allUlbs,      setAllUlbs]      = useState([]);
  const [allRegions,   setAllRegions]   = useState([]);
  const [allOffices,   setAllOffices]   = useState([]);
  const [wrdDesignations, setWrdDesignations] = useState([]);
  const [sectionOptions,  setSectionOptions]  = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [ulbOptions,      setUlbOptions]      = useState([]);
  const [regionOptions,   setRegionOptions]   = useState([]);

  useEffect(() => {
    API.get('/divisions').then((r) => setAllDivisions(Array.isArray(r?.data?.data) ? r.data.data : [])).catch(() => setAllDivisions([]));
    API.get('/ulbs').then((r) => setAllUlbs(Array.isArray(r?.data?.data) ? r.data.data : [])).catch(() => setAllUlbs([]));
    API.get('/regions').then((r) => setAllRegions(Array.isArray(r?.data?.data) ? r.data.data : [])).catch(() => setAllRegions([]));
    API.get('/offices').then((r) => setAllOffices(Array.isArray(r?.data?.data) ? r.data.data : [])).catch(() => setAllOffices([]));
    API.get('/wrd/designations').then((r) => setWrdDesignations(Array.isArray(r?.data?.data) ? r.data.data : [])).catch(() => setWrdDesignations([]));
  }, []);

  useEffect(() => {
    if (!form.district_id) {
      setSectionOptions([]); setDivisionOptions([]); setUlbOptions([]); setRegionOptions([]);
      return;
    }
    const dist = districts.find((d) => Number(d.district_id) === Number(form.district_id));
    if (!dist) return;
    const narrow = (field, master, nameCol, setter) => {
      API.get('/tanks/filter-options', { params: { field, district_name: dist.district_name }, silent: true })
        .then((r) => {
          const names = (Array.isArray(r?.data?.data) ? r.data.data : []).map((n) => String(n).trim().toLowerCase());
          const byLower = new Map(master.map((row) => [String(row[nameCol]).trim().toLowerCase(), row]));
          setter(names.map((n) => byLower.get(n)).filter(Boolean));
        })
        .catch(() => setter([]));
    };
    narrow('section', sections, 'section_name', setSectionOptions);
    // 'field' names accepted by /tanks/filter-options; if the endpoint doesn't
    // know one, the frontend falls back to showing the entire master.
    narrow('division', allDivisions, 'division_name', setDivisionOptions);
    narrow('ulb',      allUlbs,      'ulb_name',      setUlbOptions);
    narrow('region',   allRegions,   'region_name',   setRegionOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.district_id, districts, sections, allDivisions, allUlbs, allRegions]);

  // Determine the selected department's routing key (section | block).
  const selectedDept = departments.find((d) => Number(d.department_id) === Number(form.department_id));
  const routingKey = (selectedDept?.routing_key || 'block').toLowerCase();

  useEffect(() => {
    if (!authReady) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    // Only K-Labs Admin and Department Admin can manage officers.
    // Regular department (L1/L2) officers do NOT get this screen.
    if (userRole !== 'admin' && userRole !== 'dept_admin') {
      showError('You do not have access to this page');
      navigate('/dashboard');
    }
  }, [authReady, isAuthenticated, userRole, navigate]);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      API.get('/users-departments/officers').then((r) => r?.data?.data || []).catch(() => []),
      API.get('/departments').then((r) => r?.data?.data || []).catch(() => []),
      API.get('/districts').then((r) => r?.data?.data || []).catch(() => []),
      API.get('/sections').then((r) => r?.data?.data || []).catch(() => []),
    ]).then(([off, depts, dists, secs]) => {
      setOfficers(off);
      setDepartments(depts);
      setDistricts(dists);
      setSections(secs);
      setLoading(false);
    });
  };
  useEffect(fetchAll, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return officers;
    return officers.filter((o) =>
      [o.email_id, o.username, o.full_name, o.designation, o.role_type,
       o.department_name, o.district_name,
       o.section_name, o.block_name, o.division_name, o.ulb_name, o.region_name, o.office_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [officers, search]);

  const resetForm = () => {
    setForm({
      email_id: '',
      username: '',
      full_name: '',
      password: '',
      department_id: lockedDeptId || '',
      district_id: lockedDistId || '',
      section_name:  '',
      block_name:    '',
      division_name: '',
      ulb_name:      '',
      region_name:   '',
      office_name:   '',
      designation:   '',
      role_type:     '',
      level: 1,
    });
  };

  const submitForm = async () => {
    if (!form.email_id || !form.full_name || !form.department_id || !form.district_id || !form.level) {
      showError('Name, Email, Department, District and Level are all required.');
      return;
    }
    // Client-side format checks (pen-test fix 5.2). Server re-validates too.
    const emailRx = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    const nameRx  = /^[A-Za-z][A-Za-z .'\-]{0,99}$/;
    const badRx   = /[<>]|javascript:|on\w+\s*=/i;
    if (!emailRx.test(String(form.email_id).trim())) {
      showError('Please enter a valid email address.');
      return;
    }
    if (!nameRx.test(String(form.full_name).trim())) {
      showError('Full name may contain letters, spaces, dots and hyphens only.');
      return;
    }
    if (badRx.test(form.email_id) || badRx.test(form.full_name)) {
      showError('Input contains disallowed characters.');
      return;
    }
    // Strong password policy (pen-test fix 5.3). Server re-validates.
    const pwd = form.password || '';
    if (pwd.length < 8) {
      showError('Password must be at least 8 characters long.');
      return;
    }
    if (!/[a-z]/.test(pwd) || !/[A-Z]/.test(pwd) || !/[0-9]/.test(pwd) || !/[^A-Za-z0-9]/.test(pwd)) {
      showError('Password must include uppercase, lowercase, a digit and a special character.');
      return;
    }
    // Both L1 and L2 must be tied to the department's routing target.
    const req = {
      section:  { key: 'section_name',  label: 'Section' },
      block:    { key: 'block_name',    label: 'Block' },
      division: { key: 'division_name', label: 'Division' },
      ulb:      { key: 'ulb_name',      label: 'ULB' },
      region:   { key: 'region_name',   label: 'Region' },
      office:   { key: 'office_name',   label: 'Office' },
    }[routingKey];
    if (req && !form[req.key]) {
      showError(`This department routes by ${req.label} — please pick a ${req.label}.`);
      return;
    }
    setSubmitting(true);
    try {
      await API.post('/users-departments/officers', form);
      showSuccess('Officer created');
      setShowCreate(false);
      resetForm();
      fetchAll();
    } catch (err) {
      showError(err?.response?.data?.message || 'Failed to create officer');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (o) => {
    setEditingMappingId(o.mapping_id);
    setForm({
      email_id: o.email_id || '',
      username: o.username || '',
      full_name: o.full_name || '',
      password: '__EDITING__',            // placeholder — password not editable here
      department_id: o.department_id || '',
      district_id: o.district_id || '',
      section_name:  o.section_name  || '',
      block_name:    o.block_name    || '',
      division_name: o.division_name || '',
      ulb_name:      o.ulb_name      || '',
      region_name:   o.region_name   || '',
      office_name:   o.office_name   || '',
      designation:   o.designation   || '',
      role_type:     o.role_type     || '',
      level: o.level || 1,
    });
    setShowCreate(true);
  };

  const submitEdit = async () => {
    if (!editingMappingId) return;
    // Both L1 and L2 need to be tied to the routing target (section / block /
    // division / ulb / region / office depending on the dept).
    const requiredByRouting = {
      section:  { key: 'section_name',  label: 'Section'  },
      block:    { key: 'block_name',    label: 'Block'    },
      division: { key: 'division_name', label: 'Division' },
      ulb:      { key: 'ulb_name',      label: 'ULB'      },
      region:   { key: 'region_name',   label: 'Region'   },
      office:   { key: 'office_name',   label: 'Office'   },
    }[routingKey];
    if (requiredByRouting && !form[requiredByRouting.key]) {
      showError(`This department routes by ${requiredByRouting.label} — please pick a ${requiredByRouting.label}.`);
      return;
    }

    const payload = {
      full_name: form.full_name,
      level: Number(form.level),
      // Reset every routing target — only the current one gets set below.
      section_id: null, block_id: null, division_id: null,
      ulb_id: null, region_id: null, office_id: null,
    };
    if (routingKey === 'section') {
      const s = sectionOptions.find((x) => x.section_name === form.section_name)
             || sections.find((x) => x.section_name === form.section_name);
      payload.section_id = s ? s.section_id : null;
    } else if (routingKey === 'block') {
      const b = blockOptions.find((x) => x.block_name === form.block_name);
      payload.block_id = b ? b.block_id : null;
    } else if (routingKey === 'division') {
      const d = divisionOptions.find((x) => x.division_name === form.division_name);
      payload.division_id = d ? d.division_id : null;
    } else if (routingKey === 'ulb') {
      const u = ulbOptions.find((x) => x.ulb_name === form.ulb_name);
      payload.ulb_id = u ? u.ulb_id : null;
    } else if (routingKey === 'region') {
      const r = regionOptions.find((x) => x.region_name === form.region_name);
      payload.region_id = r ? r.region_id : null;
    } else if (routingKey === 'office') {
      const o = allOffices.find((x) => x.office_name === form.office_name);
      payload.office_id = o ? o.office_id : null;
      // WRD also carries designation + role_type; keep them in sync if changed.
      if (form.designation)  payload.designation = form.designation;
      if (form.role_type)    payload.role_type   = form.role_type;
    }

    setSubmitting(true);
    try {
      await API.put(`/users-departments/officers/${editingMappingId}`, payload);
      showSuccess('Officer updated');
      setShowCreate(false);
      setEditingMappingId(null);
      resetForm();
      fetchAll();
    } catch (err) {
      showError(err?.response?.data?.message || 'Failed to update officer');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Bulk upload ────────────────────────────────────────────────────
  const downloadTemplate = async () => {
    try {
      const res = await API.get('/users-departments/officers/template', {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'officer_bulk_upload_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError('Failed to download template');
    }
  };

  const runBulk = async ({ preview }) => {
    if (!bulkFile) { showError('Please choose an Excel file first.'); return; }
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const url = `/users-departments/officers/bulk-upload${preview ? '?preview=true' : ''}`;
      const res = await API.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res?.data?.data || res?.data || {};
      if (preview) setBulkPreview(data);
      else {
        setBulkImportResult(data);
        setBulkPreview(null);
        fetchAll();
      }
    } catch (err) {
      showError(err?.response?.data?.message || 'Bulk upload failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const closeBulk = () => {
    setShowBulk(false);
    setBulkFile(null);
    setBulkPreview(null);
    setBulkImportResult(null);
  };

  const handleResetPassword = async (o) => {
    const supplied = window.prompt(
      `Reset password for ${o.email_id}\n\nLeave blank to reset to the default (India@123).\nOr enter a strong password (min 8 chars, mixed case, digit, special char):`,
      ''
    );
    if (supplied === null) return; // cancelled
    try {
      const res = await API.post(`/users-departments/officers/${o.mapping_id}/reset-password`, { password: supplied || undefined });
      showSuccess(res?.data?.message || 'Password reset');
    } catch (err) {
      showError(err?.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleDelete = async (mappingId) => {
    if (!window.confirm('Deactivate this officer mapping?')) return;
    try {
      await API.delete(`/users-departments/officers/${mappingId}`);
      showSuccess('Officer mapping deactivated');
      fetchAll();
    } catch (err) {
      showError(err?.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <div className="bg-slate-100 min-h-screen pt-16 sm:pt-20">
      <div className="bg-white shadow-sm border-b-4 border-brand-accent">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center">
          <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-800 mr-3">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-500">Tamil Nadu NOC Portal</span>
          <span className="text-gray-300 mx-2">→</span>
          <span className="text-brand-700 font-medium text-sm">Department User Master</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Department User Master</h1>
            <p className="text-sm text-gray-600">
              {isAdmin
                ? 'Manage Level 1 and Level 2 officers across all departments. Every officer must be tied to a section.'
                : 'Manage Level 1 and Level 2 officers for your department. Every officer must be tied to a section.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowBulk(true)}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-medium shadow-sm"
              title="Upload many officers at once via Excel"
            >
              <Upload className="w-4 h-4" /> Bulk Upload (Excel)
            </button>
            <button
              onClick={() => { setEditingMappingId(null); resetForm(); setShowCreate(true); }}
              className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-medium shadow-sm"
            >
              <Plus className="w-4 h-4" /> Add Officer
            </button>
          </div>
        </div>

        <div className="bg-white shadow-sm border border-gray-200 mb-4 p-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email, name, department, district, or section…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-600"
            />
          </div>
        </div>

        <div className="bg-white shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-brand-800 flex justify-between items-center">
            <span className="font-semibold text-white">Officers ({filtered.length})</span>
            {loading && <span className="text-xs text-brand-100">Loading…</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-brand-600 text-white">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Officer</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Department</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">District</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Section / Block</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Level</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.length === 0 && !loading ? (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-500">No officers found.</td></tr>
                ) : filtered.map((o) => (
                  <tr key={o.mapping_id} className="odd:bg-white even:bg-brand-50 hover:bg-brand-100">
                    <td className="px-3 py-2 font-medium text-gray-900">{o.full_name || '-'}</td>
                    <td className="px-3 py-2 text-brand-800">{o.email_id}</td>
                    <td className="px-3 py-2">{o.department_name}</td>
                    <td className="px-3 py-2">{o.district_name}</td>
                    <td className="px-3 py-2">
                      {(() => {
                        // Show whichever routing target is populated. Order matches
                        // department_mst.routing_key possibilities.
                        const pairs = [
                          ['Office',   o.office_name],
                          ['Section',  o.section_name],
                          ['Block',    o.block_name],
                          ['Division', o.division_name],
                          ['ULB',      o.ulb_name],
                          ['Region',   o.region_name],
                        ];
                        const hit = pairs.find(([, v]) => v);
                        if (!hit) return <span className="text-amber-600 italic">— not set —</span>;
                        return <span><span className="text-[10px] text-gray-500 mr-1">{hit[0]}:</span>{hit[1]}</span>;
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      {/* Show WRD-style role_type when present (Master / L2 A / L2 / L1 A / L1);
                          otherwise fall back to the generic L{level} badge for other depts. */}
                      <span className="inline-flex px-2 py-0.5 text-xs font-semibold bg-brand-100 text-brand-800">
                        {o.role_type || `L${o.level}`}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          onClick={() => openEdit(o)}
                          className="inline-flex items-center gap-1 text-brand-700 hover:text-brand-900 text-xs"
                          title="Edit this officer"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button
                          onClick={() => handleResetPassword(o)}
                          className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-800 text-xs"
                          title="Reset password for this officer"
                        >
                          <KeyRound className="w-3.5 h-3.5" /> Reset PW
                        </button>
                        <button
                          onClick={() => handleDelete(o.mapping_id)}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-xs"
                          title="Deactivate this officer mapping"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Officer Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-4 sm:px-6 py-3 bg-brand-800 border-b-4 border-brand-accent flex justify-between items-center">
              <h3 className="text-base sm:text-lg font-semibold text-white">
                {editingMappingId ? 'Edit Department Officer' : 'Add Department Officer'}
              </h3>
              <button onClick={() => { setShowCreate(false); setEditingMappingId(null); }} className="text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto max-h-[70vh]">
              <Row label="Full Name *">
                <input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="input"
                  placeholder="e.g. K. Murugan, Assistant Engineer"
                />
              </Row>
              {/* WRD uses username as login id; every other dept uses email. */}
              {!editingMappingId && routingKey === 'office' && (
                <Row label="Username (login id) *">
                  <input
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="input"
                    placeholder="e.g. wrdchnrgce"
                  />
                  <p className="text-xs text-gray-500 mt-1">Officer will log in with this username.</p>
                </Row>
              )}
              {!editingMappingId && routingKey !== 'office' && (
              <Row label="Email (login id) *">
                <input
                  value={form.email_id}
                  onChange={(e) => setForm({ ...form, email_id: e.target.value })}
                  className="input"
                  type="email"
                  placeholder="officer.name@tn.gov.in"
                />
              </Row>
              )}
              {/* WRD-only: Designation dropdown auto-sets role_type + level. */}
              {routingKey === 'office' && (
                <Row label="Designation *">
                  <select
                    value={form.designation}
                    onChange={(e) => {
                      const picked = wrdDesignations.find((d) => d.designation === e.target.value);
                      setForm({
                        ...form,
                        designation: e.target.value,
                        role_type: picked?.role_type || '',
                        // Only approvers (L1, L2) get a real level; view-only
                        // roles use level 1 as a placeholder that seedApprovalChain ignores.
                        level: picked?.role_type === 'L2' ? 2 : 1,
                      });
                    }}
                    className="input"
                  >
                    <option value="">— Select Designation —</option>
                    {wrdDesignations.map((d) => (
                      <option key={d.designation} value={d.designation}>
                        {d.designation} ({d.role_type})
                      </option>
                    ))}
                  </select>
                  {form.role_type && ['L1 A', 'L2 A', 'Master'].includes(form.role_type) && (
                    <p className="text-xs text-amber-700 mt-1">
                      <b>{form.role_type}</b> is <b>view-only</b> — will see applications but cannot approve.
                    </p>
                  )}
                </Row>
              )}
              {!editingMappingId && (
              <Row label="Initial Password *">
                <input
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input"
                  type="password"
                  placeholder="Set a strong password"
                  minLength={8}
                  maxLength={128}
                />
                {/* Password-policy live hints (pen-test fix 5.3) */}
                <ul className="mt-2 text-xs space-y-0.5">
                  {(() => {
                    const pwd = form.password || '';
                    const rules = [
                      { ok: pwd.length >= 8,          text: 'At least 8 characters' },
                      { ok: /[a-z]/.test(pwd),        text: 'One lowercase letter' },
                      { ok: /[A-Z]/.test(pwd),        text: 'One uppercase letter' },
                      { ok: /[0-9]/.test(pwd),        text: 'One digit' },
                      { ok: /[^A-Za-z0-9]/.test(pwd), text: 'One special character (e.g. @ # $ !)' },
                    ];
                    return rules.map((r, i) => (
                      <li key={i} className={r.ok ? 'text-emerald-600' : 'text-gray-500'}>
                        {r.ok ? '✓' : '○'} {r.text}
                      </li>
                    ));
                  })()}
                </ul>
                <p className="text-xs text-gray-500 mt-1">
                  Share the password securely. The officer should change it after first login.
                </p>
              </Row>
              )}
              <Row label="Department *">
                <select
                  value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                  className="input"
                  disabled={isDeptAdmin || Boolean(editingMappingId)}
                >
                  <option value="">— Select Department —</option>
                  {departments.map((d) => (
                    <option key={d.department_id} value={d.department_id}>{d.department_name}</option>
                  ))}
                </select>
                {(isDeptAdmin || editingMappingId) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {editingMappingId ? 'Department cannot be changed. Delete and re-add if needed.' : 'Locked to your department.'}
                  </p>
                )}
              </Row>
              <Row label="District *">
                <select
                  value={form.district_id}
                  onChange={(e) => setForm({ ...form, district_id: e.target.value })}
                  className="input"
                  disabled={Boolean(editingMappingId)}
                >
                  <option value="">— Select District —</option>
                  {districts.map((d) => (
                    <option key={d.district_id} value={d.district_id}>{d.district_name}</option>
                  ))}
                </select>
                {editingMappingId && (
                  <p className="text-xs text-gray-500 mt-1">District cannot be changed on edit.</p>
                )}
              </Row>
              {/* Routing-target dropdown for both L1 and L2. Each level is
                  scoped to a section/block/division/ULB/region so departments
                  with per-sub-district L2 officers (rather than one district-
                  level L2) can be modelled correctly. */}
              {(() => {
                const config = {
                  section: {
                    label: 'Section',
                    stateKey: 'section_name',
                    valueKey: 'section_name',
                    idKey: 'section_id',
                    options: sectionOptions,
                    needsDistrict: true,
                  },
                  block: {
                    label: 'Block',
                    stateKey: 'block_name',
                    valueKey: 'block_name',
                    idKey: 'block_id',
                    options: blockOptions,
                    needsDistrict: true,
                  },
                  division: {
                    label: 'Division',
                    stateKey: 'division_name',
                    valueKey: 'division_name',
                    idKey: 'division_id',
                    options: divisionOptions,
                    needsDistrict: true,
                  },
                  ulb: {
                    label: 'ULB',
                    stateKey: 'ulb_name',
                    valueKey: 'ulb_name',
                    idKey: 'ulb_id',
                    options: ulbOptions,
                    needsDistrict: true,
                  },
                  region: {
                    label: 'Region',
                    stateKey: 'region_name',
                    valueKey: 'region_name',
                    idKey: 'region_id',
                    options: regionOptions,
                    needsDistrict: true,
                  },
                  office: {
                    label: 'Office',
                    stateKey: 'office_name',
                    valueKey: 'office_name',
                    idKey: 'office_id',
                    options: allOffices,
                    needsDistrict: false,
                  },
                }[routingKey];
                if (!config) return null;
                return (
                  <Row label={`${config.label} * (this department routes by ${config.label})`}>
                    <select
                      value={form[config.stateKey]}
                      onChange={(e) => setForm({ ...form, [config.stateKey]: e.target.value })}
                      className="input"
                      disabled={config.needsDistrict && !form.district_id}
                    >
                      <option value="">
                        {config.needsDistrict && !form.district_id
                          ? '— Pick a District first —'
                          : `— Select ${config.label} —`}
                      </option>
                      {config.options.map((o) => (
                        <option key={o[config.idKey]} value={o[config.valueKey]}>{o[config.valueKey]}</option>
                      ))}
                    </select>
                  </Row>
                );
              })()}
              <Row label="Level *">
                <select
                  value={form.level}
                  onChange={(e) => setForm({ ...form, level: Number(e.target.value) })}
                  className="input"
                >
                  <option value={1}>Level 1 (First-line Approver)</option>
                  <option value={2}>Level 2 (Final Approver)</option>
                </select>
              </Row>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => { setShowCreate(false); setEditingMappingId(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={editingMappingId ? submitEdit : submitForm}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-700 hover:bg-brand-800 disabled:opacity-50"
              >
                {submitting
                  ? (editingMappingId ? 'Saving…' : 'Creating…')
                  : (editingMappingId ? 'Save Changes' : 'Create Officer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-4 sm:px-6 py-3 bg-emerald-700 border-b-4 border-emerald-400 flex justify-between items-center">
              <h3 className="text-base sm:text-lg font-semibold text-white">Bulk Upload Officers (Excel)</h3>
              <button onClick={closeBulk} className="text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto max-h-[70vh]">
              {!bulkImportResult && (
                <>
                  <div className="bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
                    <p className="font-semibold mb-1">Steps</p>
                    <ol className="list-decimal ml-5 space-y-1">
                      <li>Download the template — fill one row per officer.</li>
                      <li>Upload the file — <b>Preview</b> to validate row-by-row.</li>
                      <li>Fix any errors in Excel and re-upload if needed.</li>
                      <li>When ready, click <b>Import</b> to insert the valid rows.</li>
                    </ol>
                    <p className="mt-2 text-xs">
                      Blank passwords default to <code className="bg-white px-1">India@123</code>.
                      Officers should change their password on first login.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={downloadTemplate}
                      className="inline-flex items-center gap-2 bg-white border border-emerald-600 text-emerald-700 px-3 py-2 text-sm font-medium hover:bg-emerald-50"
                    >
                      <Download className="w-4 h-4" /> Download Template
                    </button>
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkPreview(null); }}
                      className="text-sm"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => runBulk({ preview: true })}
                      disabled={bulkBusy || !bulkFile}
                      className="px-4 py-2 text-sm font-medium text-emerald-700 bg-white border border-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                    >
                      {bulkBusy ? 'Validating…' : 'Preview'}
                    </button>
                    <button
                      onClick={() => runBulk({ preview: false })}
                      disabled={bulkBusy || !bulkFile || !bulkPreview || bulkPreview.ready === 0}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {bulkBusy ? 'Importing…' : 'Import'}
                    </button>
                  </div>

                  {bulkPreview && (
                    <div className="mt-2">
                      <div className="text-sm text-gray-700 mb-2">
                        Rows: <b>{bulkPreview.total}</b> — Ready to insert: <b className="text-emerald-700">{bulkPreview.ready}</b> — Failed: <b className="text-red-700">{bulkPreview.failed}</b>
                      </div>
                      <BulkResultTable rows={bulkPreview.rows || []} />
                    </div>
                  )}
                </>
              )}

              {bulkImportResult && (
                <>
                  <div className="bg-emerald-50 border border-emerald-200 p-3 text-sm">
                    <p className="font-semibold mb-1">Import Complete</p>
                    <p>
                      Rows: <b>{bulkImportResult.total}</b> — Inserted: <b className="text-emerald-700">{bulkImportResult.inserted}</b> — Failed: <b className="text-red-700">{bulkImportResult.failed}</b>
                    </p>
                  </div>
                  <BulkResultTable rows={bulkImportResult.rows || []} />
                </>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button onClick={closeBulk} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input { width: 100%; padding: 8px 10px; font-size: 13px; border: 1px solid #d1d5db; border-radius: 6px; outline: none; }
        .input:focus { border-color: #6366f1; box-shadow: 0 0 0 2px rgba(99,102,241,0.2); }
      `}</style>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const statusBadge = (s) => {
  const map = {
    ready:    'bg-sky-100 text-sky-800',
    inserted: 'bg-emerald-100 text-emerald-700',
    failed:   'bg-red-100 text-red-700',
  };
  return `inline-flex px-2 py-0.5 text-xs font-semibold ${map[s] || 'bg-gray-100 text-gray-700'}`;
};

const BulkResultTable = ({ rows }) => (
  <div className="border border-gray-200 overflow-hidden">
    <table className="min-w-full text-sm">
      <thead className="bg-brand-600">
        <tr>
          <th className="px-3 py-2 text-left text-xs font-semibold text-white">Row</th>
          <th className="px-3 py-2 text-left text-xs font-semibold text-white">Email</th>
          <th className="px-3 py-2 text-left text-xs font-semibold text-white">Status</th>
          <th className="px-3 py-2 text-left text-xs font-semibold text-white">Reason / Note</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {rows.length === 0 && (
          <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-500">No rows</td></tr>
        )}
        {rows.map((r, i) => (
          <tr key={i} className="odd:bg-white even:bg-brand-50">
            <td className="px-3 py-2 text-gray-700">{r.row}</td>
            <td className="px-3 py-2 text-brand-800">{r.email || '-'}</td>
            <td className="px-3 py-2"><span className={statusBadge(r.status)}>{r.status}</span></td>
            <td className="px-3 py-2 text-gray-600">{r.reason || ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
